import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import ServicesMenu from "@/components/ServicesView";
import ProductsView from "@/components/ProductsView";
import IndustriesMenu from "@/components/IndustriesView";
import SAPMenu from "@/components/SAPView";
import WorkdayMenu from "@/components/WorkdayView";
import SalesforceMenu from "@/components/SalesforceView";
import SuccessStoriesMenu from "@/components/AboutUsView";
import EnterpriseView from "@/components/EnterpriseView";
import SolutionsMenu from "./SolutionsView";

const Navigation = () => {
  // Navigation component for the website
  const [isOpen, setIsOpen] = useState(false);
  const [servicesViewOpen, setServicesViewOpen] = useState(false);
  const [solutionsViewOpen, setSolutionsViewOpen] = useState(false);
  const [productsViewOpen, setProductsViewOpen] = useState(false);
  const [industriesViewOpen, setIndustriesViewOpen] = useState(false);
  const [enterpriseSolutionsOpen, setEnterpriseSolutionsOpen] = useState(false);
  const [sapViewOpen, setSapViewOpen] = useState(false);
  const [workdayViewOpen, setWorkdayViewOpen] = useState(false);
  const [salesforceViewOpen, setSalesforceViewOpen] = useState(false);
  const [successStoriesViewOpen, setSuccessStoriesViewOpen] = useState(false);
  const navigate = useNavigate();

  // Close all dropdowns function
  const closeAllDropdowns = () => {
    setServicesViewOpen(false);
    setSolutionsViewOpen(false);
    setProductsViewOpen(false);
    setIndustriesViewOpen(false);
    setEnterpriseSolutionsOpen(false);
    setSapViewOpen(false);
    setWorkdayViewOpen(false);
    setSalesforceViewOpen(false);
    setSuccessStoriesViewOpen(false);
  };

  // Handle escape key to close dropdowns
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeAllDropdowns();
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  // Close other dropdowns when opening a new one
  const handleDropdownToggle = (dropdownName: string) => {
    const wasOpen = {
      services: servicesViewOpen,
      solutions: solutionsViewOpen,
      products: productsViewOpen,
      industries: industriesViewOpen,
      enterpriseSolutions: enterpriseSolutionsOpen,
      sap: sapViewOpen,
      workday: workdayViewOpen,
      salesforce: salesforceViewOpen,
      successStories: successStoriesViewOpen,
    };

    closeAllDropdowns(); // Close all first

    // If the clicked dropdown wasn't open, open it
    if (!wasOpen[dropdownName as keyof typeof wasOpen]) {
      switch (dropdownName) {
        case "services":
          setServicesViewOpen(true);
          break;
        case "solutions":
          setSolutionsViewOpen(true);
          break;
        case "products":
          setProductsViewOpen(true);
          break;
        case "industries":
          setIndustriesViewOpen(true);
          break;
        case "enterpriseSolutions":
          setEnterpriseSolutionsOpen(true);
          break;
        case "sap":
          setSapViewOpen(true);
          break;
        case "workday":
          setWorkdayViewOpen(true);
          break;
        case "salesforce":
          setSalesforceViewOpen(true);
          break;
        case "successStories":
          setSuccessStoriesViewOpen(true);
          break;
      }
    }
  };

  const navItems = [{ href: "/", label: "Home", section: "" }];

  const endNavItems = [
    { href: "/odc", label: "Offshore Development Centers (ODC)", section: "" },
    { href: "/case-studies", label: "Case Studies", section: "" },
    { href: "/contact", label: "Contact", section: "" },
  ];

  const handleNavClick = (item: (typeof navItems)[0]) => {
    navigate(item.href);
    if (item.section && item.href === "/") {
      // Scroll to section after navigation
      setTimeout(() => {
        const element = document.querySelector(`#${item.section}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
    setIsOpen(false);
    closeAllDropdowns();
  };

  return (
    <nav
      className="fixed top-0 w-full bg-background/80 backdrop-blur-md border-b border-border shadow-lg z-50"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-14">
          <div className="flex-shrink-0">
            <Link
              to="/"
              className="flex items-center"
              aria-label="Smartborn homepage"
            >
              <img
                src="/og-image.png"
                alt="Smartborn - Leading IT Services Company Logo"
                className="h-8 w-auto"
                width="120"
                height="32"
              />
            </Link>
          </div>

          {/* Desktop Navigation - Centered */}
          <div className="hidden md:flex flex-1 justify-center">
            <div className="flex items-center space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.section}
                  onClick={() => handleNavClick(item)}
                  onMouseEnter={closeAllDropdowns}
                  className="text-foreground hover:text-primary transition-colors duration-300 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-2"
                >
                  {item.label}
                </button>
              ))}

              {/* Products Link */}
              <div
                className="relative"
                onMouseEnter={() => handleDropdownToggle("products")}
              >
                <button
                  className="text-foreground hover:text-secondary transition-colors duration-300 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-2"
                  aria-expanded={productsViewOpen}
                  aria-haspopup="true"
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                >
                  Products
                </button>
              </div>

              {/* Services Link */}
              {/* <div
                className="relative"
                onMouseEnter={() => handleDropdownToggle("services")}
              >
                <button
                  className="text-foreground hover:text-brand transition-colors duration-300 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-2"
                  aria-expanded={servicesViewOpen}
                  aria-haspopup="true"
                >
                  Services
                </button>
              </div> */}

              {/* Solutions Link */}
              <div
                className="relative"
                onMouseEnter={() => handleDropdownToggle("solutions")}
              >
                <button
                  className="text-foreground hover:text-primary transition-colors duration-300 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-2"
                  aria-expanded={solutionsViewOpen}
                  aria-haspopup="true"
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                >
                  Solution
                </button>
              </div>

              {/* Case Studies Link */}
              {/* <button
                onClick={() =>
                  handleNavClick({
                    href: "/case-studies",
                    label: "Case Studies",
                    section: "",
                  })
                }
                onMouseEnter={closeAllDropdowns}
                className="text-foreground hover:text-secondary transition-colors duration-300 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-2"
              >
                Case Studies
              </button> */}

              {/* CoEs Link */}
              <div
                className="relative"
                onMouseEnter={() => handleDropdownToggle("enterpriseSolutions")}
              >
                <button
                  className="text-foreground hover:text-primary transition-colors duration-300 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-2"
                  aria-expanded={enterpriseSolutionsOpen}
                  aria-haspopup="true"
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                >
                  CoEs
                </button>
              </div>

              {/* Industries Link */}
              <div
                className="relative"
                onMouseEnter={() => handleDropdownToggle("industries")}
              >
                <button
                  className="text-foreground hover:text-secondary transition-colors duration-300 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-2"
                  aria-expanded={industriesViewOpen}
                  aria-haspopup="true"
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                >
                  Industries
                </button>
              </div>

              {/* ODC Link */}
              <button
                onClick={() =>
                  handleNavClick({ href: "/odc", label: "ODC", section: "" })
                }
                onMouseEnter={closeAllDropdowns}
                className="text-foreground hover:text-primary transition-colors duration-300 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-2"
              >
                ODC
              </button>

              {/* About Us Link with Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => handleDropdownToggle("successStories")}
              >
                <button
                  className="text-foreground hover:text-secondary transition-colors duration-300 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-2"
                  aria-expanded={successStoriesViewOpen}
                  aria-haspopup="true"
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                >
                  About Us
                </button>
              </div>

              {/* Contact Link */}
              <button
                onClick={() =>
                  handleNavClick({
                    href: "/contact",
                    label: "Contact",
                    section: "",
                  })
                }
                onMouseEnter={closeAllDropdowns}
                className="px-4 py-1.5 rounded-full bg-duo-gradient text-white text-sm font-medium hover:shadow-elegant transition-all duration-300"
              >
                Contact
              </button>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(!isOpen)}
              aria-expanded={isOpen}
              aria-controls="mobile-menu"
              aria-label={isOpen ? "Close menu" : "Open menu"}
            >
              {isOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden" id="mobile-menu">
            <div
              className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-background border-t border-border"
              role="menu"
            >
              {navItems.map((item) => (
                <button
                  key={item.section}
                  onClick={() => handleNavClick(item)}
                  className="block w-full text-left px-3 py-2 text-base font-medium text-foreground hover:text-brand transition-colors duration-300"
                >
                  {item.label}
                </button>
              ))}

              {/* Mobile Products Link */}
              <button
                onClick={() => {
                  handleDropdownToggle("products");
                  setIsOpen(false);
                }}
                className="flex items-center gap-1 px-3 py-2 text-base font-medium text-foreground hover:text-brand transition-colors duration-300 w-full text-left"
                aria-expanded={productsViewOpen}
              >
                Products
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-300 ${productsViewOpen ? "rotate-180" : ""}`}
                />
              </button>

              {/* Mobile Services Link */}
              {/* <button
                onClick={() => {
                  handleDropdownToggle("services");
                  setIsOpen(false);
                }}
                className="flex items-center gap-1 px-3 py-2 text-base font-medium text-foreground hover:text-brand transition-colors duration-300 w-full text-left"
                aria-expanded={servicesViewOpen}
              >
                Services
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-300 ${servicesViewOpen ? "rotate-180" : ""}`}
                />
              </button> */}

              {/* Mobile Solutions Link */}
              <button
                onClick={() => {
                  handleDropdownToggle("solutions");
                  setIsOpen(false);
                }}
                className="flex items-center gap-1 px-3 py-2 text-base font-medium text-foreground hover:text-brand transition-colors duration-300 w-full text-left"
                aria-expanded={solutionsViewOpen}
              >
                Solutions
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-300 ${solutionsViewOpen ? "rotate-180" : ""}`}
                />
              </button>

              {/* Mobile Case Studies Link */}
              {/* <button
                onClick={() =>
                  handleNavClick({
                    href: "/case-studies",
                    label: "Case Studies",
                    section: "",
                  })
                }
                className="block w-full text-left px-3 py-2 text-base font-medium text-foreground hover:text-brand transition-colors duration-300"
              >
                Case Studies
              </button> */}

              {/* Mobile CoEs Link */}
              <button
                onClick={() => {
                  handleDropdownToggle("enterpriseSolutions");
                  setIsOpen(false);
                }}
                className="flex items-center gap-1 px-3 py-2 text-base font-medium text-foreground hover:text-brand transition-colors duration-300 w-full text-left"
                aria-expanded={enterpriseSolutionsOpen}
              >
                CoEs
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-300 ${enterpriseSolutionsOpen ? "rotate-180" : ""}`}
                />
              </button>

              {/* Mobile Industries Link */}
              <button
                onClick={() => {
                  handleDropdownToggle("industries");
                  setIsOpen(false);
                }}
                className="flex items-center gap-1 px-3 py-2 text-base font-medium text-foreground hover:text-brand transition-colors duration-300 w-full text-left"
                aria-expanded={industriesViewOpen}
              >
                Industries
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-300 ${industriesViewOpen ? "rotate-180" : ""}`}
                />
              </button>

              {/* Mobile ODC Link */}
              <button
                onClick={() =>
                  handleNavClick({ href: "/odc", label: "ODC", section: "" })
                }
                className="block w-full text-left px-3 py-2 text-base font-medium text-foreground hover:text-brand transition-colors duration-300"
              >
                ODC
              </button>

              {/* Mobile About Us Link */}
              <button
                onClick={() => {
                  handleDropdownToggle("successStories");
                  setIsOpen(false);
                }}
                className="flex items-center gap-1 px-3 py-2 text-base font-medium text-foreground hover:text-brand transition-colors duration-300 w-full text-left"
                aria-expanded={successStoriesViewOpen}
              >
                About Us
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-300 ${successStoriesViewOpen ? "rotate-180" : ""}`}
                />
              </button>

              {/* Mobile Contact Link */}
              <button
                onClick={() =>
                  handleNavClick({
                    href: "/contact",
                    label: "Contact",
                    section: "",
                  })
                }
                className="block w-full text-left px-3 py-2 text-base font-medium text-foreground hover:text-brand transition-colors duration-300"
              >
                Contact
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Dropdown Menus - Rendered outside navigation sections so they work on both desktop and mobile */}
      <ServicesMenu
        isOpen={servicesViewOpen}
        onClose={() => setServicesViewOpen(false)}
      />
      <SolutionsMenu
        isOpen={solutionsViewOpen}
        onClose={() => setSolutionsViewOpen(false)}
      />
      <ProductsView
        isOpen={productsViewOpen}
        onClose={() => setProductsViewOpen(false)}
      />
      <IndustriesMenu
        isOpen={industriesViewOpen}
        onClose={() => setIndustriesViewOpen(false)}
      />
      <SAPMenu isOpen={sapViewOpen} onClose={() => setSapViewOpen(false)} />
      <WorkdayMenu
        isOpen={workdayViewOpen}
        onClose={() => setWorkdayViewOpen(false)}
      />
      <SalesforceMenu
        isOpen={salesforceViewOpen}
        onClose={() => setSalesforceViewOpen(false)}
      />
      <SuccessStoriesMenu
        isOpen={successStoriesViewOpen}
        onClose={() => setSuccessStoriesViewOpen(false)}
      />
      <EnterpriseView
        isOpen={enterpriseSolutionsOpen}
        onClose={() => setEnterpriseSolutionsOpen(false)}
      />
    </nav>
  );
};

export default Navigation;
